import { useCallback, useEffect, useState } from "react";
import { Box, Chip, Dialog, IconButton, Tooltip } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Image, Video, buildSrc } from "@imagekit/react";
import { toIkSrc, IK_ENDPOINT } from "../../../../helpers/imagekit";

function getFileName(url) {
  if (!url) return "File";
  const parts = url.split("/");
  return decodeURIComponent(parts[parts.length - 1]) || "File";
}

function isVideoUrl(url) {
  return /\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(url);
}

/** Blurred low-quality placeholder URL via ImageKit */
function placeholderSrc(url) {
  return buildSrc({
    urlEndpoint: IK_ENDPOINT,
    src: toIkSrc(url),
    transformation: [{ quality: 10, blur: 80, width: 20 }],
  });
}

/** Full-screen lightbox */
function Lightbox({ urls, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const url = urls[index];
  const hasMultiple = urls.length > 1;

  const prev = (e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + urls.length) % urls.length); };
  const next = (e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % urls.length); };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiple) setIndex((i) => (i - 1 + urls.length) % urls.length);
      if (e.key === "ArrowRight" && hasMultiple) setIndex((i) => (i + 1) % urls.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, hasMultiple, urls.length]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          m: 0,
          maxWidth: "100vw",
          maxHeight: "100vh",
        },
      }}
      sx={{ "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.92)" } }}
    >
      {/* Close */}
      <IconButton
        onClick={onClose}
        sx={{ position: "fixed", top: 16, right: 16, color: "#fff", bgcolor: "rgba(0,0,0,0.4)", zIndex: 10, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
      >
        <CloseIcon />
      </IconButton>

      {/* Counter */}
      {hasMultiple && (
        <Box sx={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", color: "#fff", fontWeight: 600, fontSize: "0.875rem", zIndex: 10, bgcolor: "rgba(0,0,0,0.4)", px: 1.5, py: 0.5, borderRadius: 5 }}>
          {index + 1} / {urls.length}
        </Box>
      )}

      {/* Prev */}
      {hasMultiple && (
        <IconButton onClick={prev} sx={{ position: "fixed", left: 12, top: "50%", transform: "translateY(-50%)", color: "#fff", bgcolor: "rgba(0,0,0,0.4)", zIndex: 10, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
          <ArrowBackIosNewIcon />
        </IconButton>
      )}

      {/* Media */}
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100vw", height: "100vh" }}
        onClick={onClose}
      >
        {isVideoUrl(url) ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8 }}
          />
        ) : (
          <Image
            src={toIkSrc(url)}
            alt=""
            transformation={[{ width: 2400, quality: 90, format: "auto", cropMode: "at_max" }]}
            responsive={false}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8, display: "block" }}
          />
        )}
      </Box>

      {/* Next */}
      {hasMultiple && (
        <IconButton onClick={next} sx={{ position: "fixed", right: 12, top: "50%", transform: "translateY(-50%)", color: "#fff", bgcolor: "rgba(0,0,0,0.4)", zIndex: 10, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
          <ArrowForwardIosIcon />
        </IconButton>
      )}
    </Dialog>
  );
}

function IKMediaItem({ url, aspectPadding, onPreview }) {
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const imgRef = useCallback((el) => {
    if (!el) return;
    if (el.complete) setShowPlaceholder(false);
  }, []);

  if (isVideoUrl(url)) {
    const poster = buildSrc({
      urlEndpoint: IK_ENDPOINT,
      src: `${toIkSrc(url)}/ik-thumbnail.jpg`,
    });
    return (
      <Box
        sx={{
          position: "relative",
          paddingBottom: aspectPadding,
          backgroundColor: "grey.900",
          overflow: "hidden",
        }}
      >
        <Video
          src={toIkSrc(url)}
          controls
          playsInline
          preload="none"
          poster={poster}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        paddingBottom: aspectPadding,
        backgroundColor: "action.hover",
        overflow: "hidden",
        cursor: "zoom-in",
      }}
      onClick={onPreview}
    >
      <Image
        src={toIkSrc(url)}
        alt=""
        ref={imgRef}
        loading="lazy"
        transformation={[{ width: 400, quality: 40, format: "auto", cropMode: "at_max" }]}
        responsive={false}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "opacity 0.3s, transform 0.2s",
          backgroundImage: showPlaceholder ? `url(${placeholderSrc(url)})` : "none",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
        onLoad={() => setShowPlaceholder(false)}
      />
    </Box>
  );
}

function MediaGrid({ mediaUrls }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  if (!mediaUrls?.length) return null;
  const count = mediaUrls.length;

  const gridTemplateMap = {
    1: "1fr",
    2: "1fr 1fr",
    3: "1fr 1fr",
    4: "1fr 1fr",
  };
  const gridTemplate = gridTemplateMap[Math.min(count, 4)] || "1fr 1fr";
  const aspectPadding = count === 1 ? "56.25%" : "100%";
  const items = mediaUrls.slice(0, 4);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap: "3px",
          borderRadius: 2,
          overflow: "hidden",
          mt: 1,
        }}
      >
        {items.map((url, i) => (
          <IKMediaItem
            key={i}
            url={url}
            aspectPadding={aspectPadding}
            onPreview={!isVideoUrl(url) ? () => setLightboxIndex(i) : undefined}
          />
        ))}
      </Box>

      {lightboxIndex !== null && (
        <Lightbox
          urls={items.filter((u) => !isVideoUrl(u))}
          startIndex={items.filter((u) => !isVideoUrl(u)).indexOf(items[lightboxIndex])}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

function AttachmentList({ attachmentUrls }) {
  if (!attachmentUrls?.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
      {attachmentUrls.map((url, i) => (
        <Chip
          key={i}
          icon={<InsertDriveFileOutlinedIcon />}
          label={getFileName(url)}
          size="small"
          variant="outlined"
          deleteIcon={
            <Tooltip title="Download">
              <DownloadOutlinedIcon fontSize="small" />
            </Tooltip>
          }
          onDelete={() => window.open(url, "_blank", "noopener,noreferrer")}
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          sx={{ cursor: "pointer", maxWidth: 220 }}
        />
      ))}
    </Box>
  );
}

export default function PostMediaGallery({ data }) {
  const mediaUrls = data?.media_urls || [];
  const attachmentUrls = data?.attachment_urls || [];
  if (!mediaUrls.length && !attachmentUrls.length) return null;

  return (
    <Box>
      <MediaGrid mediaUrls={mediaUrls} />
      <AttachmentList attachmentUrls={attachmentUrls} />
    </Box>
  );
}


